import type { CoachGender, CoachTone } from '../stores/coach.store';

/**
 * Catalogue of audio asset paths for every coach (gender × tone). Phase 1
 * ships silent MP3s of the correct durations so the player UX is honest
 * (timers tick against real metadata); phase 2 swaps the same files for
 * recorded voice without touching consumers.
 *
 * Run `node apps/web/scripts/generate-mock-audio.mjs` once to materialise
 * the silent placeholders into `apps/web/public/audio/`.
 */

/** Short greeting snippet — played in the reveal step preview. */
export type CoachSnippet = 'opener' | 'breath' | 'encouragement' | 'edge';

export interface CoachSnippetMeta {
  /** Path relative to the web app's public root. */
  url: string;
  /** Duration in seconds — kept here so the UI shows it before metadata loads. */
  durationS: number;
}

export const COACH_SNIPPET_DURATIONS: Record<CoachSnippet, number> = {
  opener: 15,
  breath: 8,
  encouragement: 8,
  edge: 12,
};

/** Long guided session played in the ritual's third level. */
export const GUIDED_SESSION_DURATION_S = 240;

/** Cooldown audio played in the afterglow screen after the ritual ends. */
export const AFTERGLOW_DURATION_S = 120;

// Example audio: until per-coach recordings exist, every playback site points
// at the single bundled sample so audio is actually audible everywhere.
const EXAMPLE_AUDIO_URL = '/sexy_audio.mp3';

function snippetUrl(gender: CoachGender, tone: CoachTone, snippet: CoachSnippet): string {
  void gender;
  void tone;
  void snippet;
  return EXAMPLE_AUDIO_URL;
}

function sessionUrl(gender: CoachGender, tone: CoachTone): string {
  void gender;
  void tone;
  return EXAMPLE_AUDIO_URL;
}

function afterglowUrl(gender: CoachGender, tone: CoachTone): string {
  void gender;
  void tone;
  return EXAMPLE_AUDIO_URL;
}

export function getCoachSnippet(
  gender: CoachGender,
  tone: CoachTone,
  snippet: CoachSnippet,
): CoachSnippetMeta {
  return {
    url: snippetUrl(gender, tone, snippet),
    durationS: COACH_SNIPPET_DURATIONS[snippet],
  };
}

export function getGuidedSession(
  gender: CoachGender,
  tone: CoachTone,
): CoachSnippetMeta {
  return {
    url: sessionUrl(gender, tone),
    durationS: GUIDED_SESSION_DURATION_S,
  };
}

export function getAfterglowAudio(
  gender: CoachGender,
  tone: CoachTone,
): CoachSnippetMeta {
  return {
    url: afterglowUrl(gender, tone),
    durationS: AFTERGLOW_DURATION_S,
  };
}

/** Calm, neutral cooldown — same for every coach. Used when the safe-exit */
/** path triggers the afterglow so the audio stays medically reassuring. */
export const SAFE_EXIT_AFTERGLOW: CoachSnippetMeta = {
  url: EXAMPLE_AUDIO_URL,
  durationS: AFTERGLOW_DURATION_S,
};
